import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ApplicantInsuranceIntentCard } from './sections/ApplicantInsuranceIntentCard';
import { CoiAcquisitionSection } from './sections/CoiAcquisitionSection';
import { VendorOpportunity, CreditApplication } from '@trnsact/trnsact-shared-types';
import { InsuranceStatusBox } from './common/InsuranceStatusBox';
import { insuranceSelector } from '../model/selectors';
import { useSelector } from 'react-redux';
import { LenderRequirementsSection } from './sections/LenderRequirements/LenderRequirementsSection';
import { InsuranceSelectSubmissionForm } from '../types';
import { useForm } from 'react-hook-form';

interface Props {
  vo: VendorOpportunity;
  creditApp: CreditApplication;
  applicantIntent?: 'Requested Quote' | 'Providing Own Coverage';
  insuranceStatus?: 'Not Started' | 'In Progress' | 'Completed';
  coiStatus?: 'Not Received' | 'Received' | 'Sent To Lender';
  onSendInsuranceQuoteToApplicant: () => void;
  onAddAgentDetails?: () => void;
  onUploadCoiManually?: () => void;
  onEnterRequirements?: () => void;
  onSelectSubmission?: () => void;
  onSetupFinanceProgram?: () => void;
}

export const InsuranceOverviewPanel = ({
  applicantIntent = 'Requested Quote',
  coiStatus = 'Not Received',
  onSendInsuranceQuoteToApplicant,
  onAddAgentDetails,
  onUploadCoiManually,
  onEnterRequirements,
  onSelectSubmission,
  onSetupFinanceProgram,
}: Props) => {
  const classes = useStyles();

  const selectedSubmission = useSelector(insuranceSelector.selectedSubmission);
  const selectedFinanceProgram = useSelector(insuranceSelector.selectedFinanceProgram);

  const insuranceQuote = useSelector(insuranceSelector.insuranceQuote);

  const form = useForm<InsuranceSelectSubmissionForm>({
    defaultValues: {
      submissionId: '',
      financeProgramId: '',

      coverageRequirements:
        selectedFinanceProgram?.insuranceCoverage?.coverageRequirements ||
        insuranceQuote?.insuranceCoverage?.coverageRequirements ||
        '',
      certificateRequirements:
        selectedFinanceProgram?.insuranceCoverage?.certificateRequirements ||
        insuranceQuote?.insuranceCoverage?.certificateRequirements ||
        '',
      otherInstructions:
        selectedFinanceProgram?.insuranceCoverage?.otherInstructions ||
        insuranceQuote?.insuranceCoverage?.otherInstructions ||
        '',
    },
  });

  return (
    <div className={classes.insurancePanel}>
      {/* Header row: left title, right-aligned status (no overlap) */}
      <Box className={classes.headerRow}>
        <Typography variant="h6" component="p" className={classes.title}>
          Insurance
        </Typography>

        <Box className={classes.insuranceStatus}>
          <InsuranceStatusBox label="Insurance Status" statusText="In Progress" />
        </Box>
      </Box>

      <Box className={classes.root}>
        <ApplicantInsuranceIntentCard intent={applicantIntent} />

        <Divider className={classes.divider} />

        <CoiAcquisitionSection
          coiStatus={coiStatus}
          onSendInsuranceQuoteToApplicant={onSendInsuranceQuoteToApplicant}
          onAddAgentDetails={onAddAgentDetails}
          onUploadCoiManually={onUploadCoiManually}
        />

        <Divider className={classes.divider} />

        <LenderRequirementsSection
          onEnterRequirements={onEnterRequirements}
          onSelectSubmission={onSelectSubmission}
          onSetupFinanceProgram={onSetupFinanceProgram}
          selectedFinanceProgram={selectedFinanceProgram}
          selectedSubmission={selectedSubmission}
          insuranceQuote={insuranceQuote}
        />
      </Box>
    </div>
  );
};

const useStyles = makeStyles({
  insurancePanel: {
    backgroundColor: '#FFFFFF',
    padding: '16px',
    borderRadius: '4px',
  },

  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 16,
  },

  title: {
    flex: '0 0 auto',
  },

  // Right-aligned like "Certificate of Insurance Status", without overlap
  insuranceStatus: {
    flex: '1 1 auto',
    display: 'flex',
    justifyContent: 'flex-end',
    textAlign: 'right',
    fontWeight: 400,
    fontSize: '14px',
    color: '#666666',
  },

  root: {
    width: '100%',
  },

  divider: {
    margin: '16px 0',
  },
});
